import React, { useMemo, useState } from 'react';
import { SideBarModal } from '@/components/Modal';
import { Button, SearchableDropdown } from '@/components';
import { RepossessionOpsRecommendation } from '@/types/common';

interface EscalateToManagerModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (payload: {
    searchEfforts: string;
    escalationReason: string;
    lastKnownInfo: string;
    opsRecommendation: RepossessionOpsRecommendation;
    estimatedCost?: number;
  }) => void;
  isSubmitting?: boolean;
}

const EscalateToManagerModal: React.FC<EscalateToManagerModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  isSubmitting = false,
}) => {
  const recommendationOptions = [
    {
      value: RepossessionOpsRecommendation.DECLARE_TOTAL_LOSS,
      label: 'DECLARE TOTAL LOSS - NO HOPE FOR RECOVERY',
    },
    {
      value: RepossessionOpsRecommendation.PROCEED_LEGAL_ACTION,
      label: 'PROCEED LEGAL ACTION',
    },
    {
      value: RepossessionOpsRecommendation.FILE_INSURANCE_CLAIM,
      label: 'FILE INSURANCE CLAIM',
    },
    {
      value: RepossessionOpsRecommendation.CONTINUE_SEARCH_WITH_BUDGET,
      label: 'CONTINUE SEARCH WITH BUDGET',
    },
  ];
  const [searchEfforts, setSearchEfforts] = useState('');
  const [escalationReason, setEscalationReason] = useState('');
  const [lastKnownInfo, setLastKnownInfo] = useState('');
  const [recommendation, setRecommendation] = useState<
    RepossessionOpsRecommendation | ''
  >('');
  const [estimatedCost, setEstimatedCost] = useState('');
  const [hasSubmitted, setHasSubmitted] = useState(false);

  const formatter = useMemo(
    () =>
      new Intl.NumberFormat('id-ID', {
        style: 'currency',
        currency: 'IDR',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }),
    []
  );

  const searchEffortsLength = searchEfforts.trim().length;
  const escalationReasonLength = escalationReason.trim().length;
  const lastKnownInfoLength = lastKnownInfo.trim().length;

  const isSearchEffortsInvalid =
    searchEffortsLength > 0 && searchEffortsLength < 50;
  const isEscalationReasonInvalid =
    escalationReasonLength > 0 && escalationReasonLength < 50;
  const isLastKnownInfoInvalid =
    lastKnownInfoLength > 0 && lastKnownInfoLength < 50;

  const handleClose = () => {
    setSearchEfforts('');
    setEscalationReason('');
    setLastKnownInfo('');
    setRecommendation('');
    setEstimatedCost('');
    onClose();
  };

  const mapRecommendationToPayload = (
    value: RepossessionOpsRecommendation
  ): RepossessionOpsRecommendation => {
    switch (value) {
      case RepossessionOpsRecommendation.DECLARE_TOTAL_LOSS:
      case RepossessionOpsRecommendation.PROCEED_LEGAL_ACTION:
      case RepossessionOpsRecommendation.FILE_INSURANCE_CLAIM:
      case RepossessionOpsRecommendation.CONTINUE_SEARCH_WITH_BUDGET:
        return value;
      default:
        return value;
    }
  };

  const handleSubmit = () => {
    setHasSubmitted(true);
    if (!recommendation) return;
    const parsedCost = Number(estimatedCost);
    const shouldIncludeCost =
      recommendation ===
      RepossessionOpsRecommendation.CONTINUE_SEARCH_WITH_BUDGET;

    if (shouldIncludeCost && !estimatedCost.trim()) return;

    onSubmit({
      searchEfforts: searchEfforts.trim(),
      escalationReason: escalationReason.trim(),
      lastKnownInfo: lastKnownInfo.trim(),
      opsRecommendation: mapRecommendationToPayload(recommendation),
      estimatedCost: shouldIncludeCost
        ? Number.isNaN(parsedCost)
          ? 0
          : parsedCost
        : undefined,
    });
  };

  const isMinCharsMet =
    searchEfforts.trim().length >= 50 &&
    escalationReason.trim().length >= 50 &&
    lastKnownInfo.trim().length >= 50;

  const formatCostValue = () => {
    if (!estimatedCost) return '';
    const numericValue = Number(estimatedCost);
    if (Number.isNaN(numericValue)) return '';
    return formatter.format(numericValue);
  };

  const shouldIncludeCost =
    recommendation ===
    RepossessionOpsRecommendation.CONTINUE_SEARCH_WITH_BUDGET;
  const isRecommendationInvalid = !recommendation;
  const isEstimatedCostInvalid = shouldIncludeCost && !estimatedCost.trim();

  const isFormValid =
    isMinCharsMet && !isRecommendationInvalid && !isEstimatedCostInvalid;

  return (
    <SideBarModal
      isOpen={isOpen}
      onClose={handleClose}
      position="right"
      width="sm"
      maxHeight="screen"
      header={
        <div>
          <div className="text-base font-semibold text-gray-900">
            Escalate to Manager
          </div>
          <div className="text-xs text-gray-500">
            Provide justification and search history to escalate this case.
          </div>
        </div>
      }
      footer={
        <div className="flex w-full justify-end">
          <Button
            disabled={!isFormValid || isSubmitting}
            className="w-full"
            variant={'primary'}
            onClick={handleSubmit}
          >
            {isSubmitting ? 'Saving...' : 'Save'}
          </Button>
        </div>
      }
      className="max-w-[400px]"
    >
      <div className="bg-gray-50 px-4 py-2 text-[12px] font-medium text-gray-500 rounded-md">
        Detail
      </div>
      <div className="px-6 py-4">
        <div className="space-y-4">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Search Efforts Made<span className="text-red-500"> *</span>
            </label>
            <div className="relative">
              <textarea
                minLength={50}
                value={searchEfforts}
                onChange={(e) => setSearchEfforts(e.target.value)}
                placeholder="Describe all search efforts made (e.g. visited last known location, contacted mitra’s family, checked with local authorities, GPS tracking...)"
                rows={4}
                className={`w-full rounded-md border px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-purple-200 ${
                  isSearchEffortsInvalid
                    ? 'border-red-500 focus:ring-red-200'
                    : 'border-gray-200'
                }`}
              />
              <div className="absolute bottom-2 right-3 text-[10px] text-gray-400">
                {Math.min(searchEfforts.length, 200)}/200
              </div>
              {isSearchEffortsInvalid && (
                <div className="mt-1 text-[11px] text-red-500">
                  Minimum 50 characters
                </div>
              )}
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Why Escalating Now<span className="text-red-500"> *</span>
            </label>
            <div className="relative">
              <textarea
                minLength={50}
                value={escalationReason}
                onChange={(e) => setEscalationReason(e.target.value)}
                placeholder="Explain why you're escalating at this point (e.g. exhausted all search options, no leads after X days, mitra confirmed absconded...)"
                rows={4}
                className={`w-full rounded-md border px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-purple-200 ${
                  isEscalationReasonInvalid
                    ? 'border-red-500 focus:ring-red-200'
                    : 'border-gray-200'
                }`}
              />
              <div className="absolute bottom-2 right-3 text-[10px] text-gray-400">
                {Math.min(escalationReason.length, 200)}/200
              </div>
              {isEscalationReasonInvalid && (
                <div className="mt-1 text-[11px] text-red-500">
                  Minimum 50 characters
                </div>
              )}
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Last Known Information<span className="text-red-500"> *</span>
            </label>
            <div className="relative">
              <textarea
                minLength={50}
                value={lastKnownInfo}
                onChange={(e) => setLastKnownInfo(e.target.value)}
                placeholder="Any final information about vehicle or mitra (last GPS location, witness reports, mitra’s last communication...)"
                rows={4}
                className={`w-full rounded-md border px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-purple-200 ${
                  isLastKnownInfoInvalid
                    ? 'border-red-500 focus:ring-red-200'
                    : 'border-gray-200'
                }`}
              />
              <div className="absolute bottom-2 right-3 text-[10px] text-gray-400">
                {Math.min(lastKnownInfo.length, 200)}/200
              </div>
              {isLastKnownInfoInvalid && (
                <div className="mt-1 text-[11px] text-red-500">
                  Minimum 50 characters
                </div>
              )}
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Recommendation to Manager<span className="text-red-500"> *</span>
            </label>
            <div className="relative">
              <SearchableDropdown
                options={recommendationOptions}
                value={recommendation || undefined}
                onChange={(value) =>
                  setRecommendation(value as RepossessionOpsRecommendation)
                }
                placeholder="Select recommendation"
                className="w-full"
                error={hasSubmitted && isRecommendationInvalid}
              />
              {hasSubmitted && isRecommendationInvalid && (
                <div className="mt-1 text-[11px] text-red-500">
                  Please select a recommendation.
                </div>
              )}
            </div>
          </div>

          {recommendation ===
            RepossessionOpsRecommendation.CONTINUE_SEARCH_WITH_BUDGET && (
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Estimated Cost<span className="text-red-500"> *</span>
              </label>
              <input
                type="text"
                inputMode="numeric"
                value={formatCostValue()}
                onChange={(e) => {
                  const digitsOnly = e.target.value.replace(/[^\d]/g, '');
                  setEstimatedCost(digitsOnly);
                }}
                placeholder="e.g. 29301"
                className={`w-full rounded-md border px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-purple-200 ${
                  hasSubmitted && isEstimatedCostInvalid
                    ? 'border-red-500 focus:ring-red-200'
                    : 'border-gray-200'
                }`}
              />
              <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-sm text-gray-400">
                IDR
              </span>
              {hasSubmitted && isEstimatedCostInvalid && (
                <div className="mt-1 text-[11px] text-red-500">
                  Estimated cost is required.
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </SideBarModal>
  );
};

export default EscalateToManagerModal;
